import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { TERRITORIES, DEFAULT_TERRITORY, isTerritoryCode, type TerritoryConfig, type TerritoryCode } from '@shared/territories';

interface TerritoryContextType {
  territory: TerritoryConfig;
  territoryCode: TerritoryCode;
  setTerritory: (code: TerritoryCode) => void;
  isDetecting: boolean;
  showSelector: boolean;
}

const TerritoryContext = createContext<TerritoryContextType>({
  territory: TERRITORIES[DEFAULT_TERRITORY],
  territoryCode: DEFAULT_TERRITORY,
  setTerritory: () => {},
  isDetecting: true,
  showSelector: false,
});

// Privacy-restrictive browser configs (private mode, blocked storage, certain
// corp policies) expose `window.localStorage` as null. Guard every access so
// these users get the geo-IP fallback instead of a TypeError that breaks the
// whole TerritoryProvider render.
function safeLocalGet(key: string): string | null {
  try {
    return typeof window !== "undefined" ? window.localStorage?.getItem(key) ?? null : null;
  } catch {
    return null;
  }
}

function safeLocalSet(key: string, value: string): void {
  try {
    if (typeof window !== "undefined") window.localStorage?.setItem(key, value);
  } catch {
    /* ignore: territory will just re-detect next session */
  }
}

export function TerritoryProvider({ children }: { children: ReactNode }) {
  const [territoryCode, setTerritoryCode] = useState<TerritoryCode>(DEFAULT_TERRITORY);
  const [isDetecting, setIsDetecting] = useState(true);
  const [showSelector, setShowSelector] = useState(false);

  useEffect(() => {
    // 1. Check URL param ?country=xx
    const params = new URLSearchParams(window.location.search);
    const countryParam = params.get('country');
    if (countryParam && isTerritoryCode(countryParam)) {
      const code = countryParam.toLowerCase() as TerritoryCode;
      setTerritoryCode(code);
      safeLocalSet('edufocal_territory', code);
      setShowSelector(false);
      setIsDetecting(false);
      return;
    }

    // 2. Check localStorage
    const stored = safeLocalGet('edufocal_territory');
    if (stored && isTerritoryCode(stored)) {
      setTerritoryCode(stored as TerritoryCode);
      setShowSelector(false);
      setIsDetecting(false);
      return;
    }

    // 3. Geo-IP detection
    fetch('/api/territory')
      .then(r => r.json())
      .then(data => {
        if (typeof data.territory === 'string' && isTerritoryCode(data.territory)) {
          const code = data.territory.toLowerCase() as TerritoryCode;
          setTerritoryCode(code);
          safeLocalSet('edufocal_territory', code);
          setShowSelector(false);
        } else {
          // Couldn't detect a supported territory - show selector
          setShowSelector(true);
        }
      })
      .catch(() => {
        // Geo-IP failed - show selector so user can pick
        setShowSelector(true);
      })
      .finally(() => setIsDetecting(false));
  }, []);

  const setTerritory = (code: TerritoryCode) => {
    setTerritoryCode(code);
    safeLocalSet('edufocal_territory', code);
    setShowSelector(false);
  };

  return (
    <TerritoryContext.Provider value={{
      territory: TERRITORIES[territoryCode],
      territoryCode,
      setTerritory,
      isDetecting,
      showSelector,
    }}>
      {children}
    </TerritoryContext.Provider>
  );
}

export function useTerritory() {
  return useContext(TerritoryContext);
}
